import React from 'react';
import PageHeader from '../components/PageHeader';
import defaultArt from '../assets/images/dj_kip_art.png';
import {
  InternalPageMeta,
  InternalPageArt,
  InternalPageText,
} from '../components/ui/page';
import SetsSection from '../components/sections/SetsSection';
import TracksSection from '../components/sections/TracksSection';
import IdsSection from '../components/sections/IdsSection';
import ArtistsSection from '../components/sections/ArtistsSection';
import LabelsSection from '../components/sections/LabelsSection';
import { useUser } from '../context/UserProvider';
import ShowMore from '../components/ShowMore';
import TabsContainer from '../components/TabsContainer';
import { useState, useEffect } from 'react';
import { useApi } from '../context/ApiProvider';
import { useSnackbar } from 'notistack';
// MUI imports
import {
  IconButton,
  Popover,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  Button,
  Stack,
  TextField,
  FormGroup,
  FormLabel,
  Tabs,
  Tab,
  Dialog,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import OptionsMenu from '../components/OptionsMenu';
import EditIcon from '@mui/icons-material/Edit';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SocialLinks from '../components/SocialLinks';

const SOUND_CLOUD_OPTIONS = [
  { label: 'Listener', value: 'listener' },
  { label: 'DJ', value: 'dj' },
  { label: 'Producer', value: 'producer' },
  { label: 'Label', value: 'label' },
  { label: 'Curator / Other', value: 'curator' },
];

const Profile = () => {
  const api = useApi();
  const { user, setUser } = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const { about_me, username, avatar_url, id } = user || {};

  // Notification preferences state
  const [notificationPreferences, setNotificationPreferences] = useState({
    notification_timeframe: user?.notification_timeframe || 'daily',
    notification_preferences_enabled:
      user?.notification_preferences_enabled ?? true,
  });
  // Popover state
  const [anchorEl, setAnchorEl] = useState(null);

  // Add tab state for popover menu
  const [popoverTab, setPopoverTab] = useState(0);

  // SoundCloud Import form state
  const [soundCloudUrl, setSoundCloudUrl] = useState(
    user?.soundcloud_url || ''
  );
  const [soundCloudRoles, setSoundCloudRoles] = useState(
    user?.user_type &&
      typeof user.user_type === 'string' &&
      user.user_type.length > 0
      ? user.user_type.split(',')
      : ['listener']
  );

  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [editProfile, setEditProfile] = useState({
    username: user?.username || '',
    about_me: user?.about_me || '',
    soundcloud_url: user?.soundcloud_url || '',
    user_type:
      user?.user_type &&
      typeof user.user_type === 'string' &&
      user.user_type.length > 0
        ? user.user_type.split(',')
        : ['listener'],
  });

  // Keep form state in sync with user context when user changes (e.g. after update)
  useEffect(() => {
    setSoundCloudUrl(user?.soundcloud_url || '');
    setSoundCloudRoles(
      user?.user_type &&
        typeof user.user_type === 'string' &&
        user.user_type.length > 0
        ? user.user_type.split(',')
        : ['listener']
    );
  }, [user]);

  const handleGearClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Sync form state to latest user context when opening
    setNotificationPreferences({
      notification_timeframe: user?.notification_timeframe || 'daily',
      notification_preferences_enabled:
        user?.notification_preferences_enabled ?? true,
    });
    setSoundCloudUrl(user?.soundcloud_url || '');
    setSoundCloudRoles(
      user?.user_type &&
        typeof user.user_type === 'string' &&
        user.user_type.length > 0
        ? user.user_type.split(',')
        : ['listener']
    );
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const handlePopoverTabChange = (event, newValue) => {
    setPopoverTab(newValue);
  };

  const handleCheckboxChange = (e) => {
    setNotificationPreferences((prev) => ({
      ...prev,
      notification_preferences_enabled: e.target.checked,
    }));
  };
  const handleSelectChange = (e) => {
    setNotificationPreferences((prev) => ({
      ...prev,
      notification_timeframe: e.target.value,
    }));
  };

  const handleSoundCloudRoleChange = (event) => {
    const { value, checked } = event.target;
    setSoundCloudRoles((prev) => {
      if (checked) {
        return [...new Set([...prev, value])];
      } else {
        return prev.filter((role) => role !== value);
      }
    });
  };

  const handleSoundCloudSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put('/me/soundcloud-import', {
        soundcloud_url: soundCloudUrl,
        user_type: soundCloudRoles,
      });
      setSoundCloudUrl(response.data?.soundcloud_url || '');
      setSoundCloudRoles(
        Array.isArray(response.data?.user_type) &&
          response.data.user_type.length > 0
          ? response.data.user_type
          : ['listener']
      );
      setUser({
        ...user,
        soundcloud_url: response.data?.soundcloud_url || '',
        user_type: (response.data?.user_type || ['listener']).join(','),
      });
      enqueueSnackbar(
        `Your SoundCloud was updated to ${
          response.data?.soundcloud_url || soundCloudUrl
        }`,
        { variant: 'success' }
      );
      handlePopoverClose();
    } catch (error) {
      // Optionally show an error message
    }
  };

  const handleNotificationPreferencesUpdate = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put(
        '/me/notification-preferences',
        notificationPreferences
      );

      // Fetch fresh user data to ensure we have the most up-to-date information
      const userResponse = await api.get('/me');
      if (userResponse.ok) {
        setUser(userResponse.body);
      } else {
        // Fallback: manually update user context with response data
        setUser({
          ...user,
          notification_timeframe: response.data?.notification_timeframe,
          notification_preferences_enabled:
            response.data?.notification_preferences_enabled,
        });
      }

      enqueueSnackbar(
        `Your notifications were updated to ${
          response.data?.notification_timeframe ||
          notificationPreferences.notification_timeframe
        }`,
        { variant: 'success' }
      );
    } catch (error) {
      // Optionally show an error message
    } finally {
      setNotificationModalOpen(false);
    }
  };

  const handleEditProfileChange = (e) => {
    const { name, value } = e.target;
    setEditProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditProfileUserTypeChange = (event) => {
    const { value, checked } = event.target;
    setEditProfile((prev) => {
      if (checked) {
        return { ...prev, user_type: [...new Set([...prev.user_type, value])] };
      } else {
        return {
          ...prev,
          user_type: prev.user_type.filter((role) => role !== value),
        };
      }
    });
  };

  const handleEditProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put('/me/profile-update', {
        about_me: editProfile.about_me,
        soundcloud_url: editProfile.soundcloud_url,
        user_type: editProfile.user_type,
      });

      // Fetch fresh user data to ensure we have the most up-to-date information
      const userResponse = await api.get('/me');
      if (userResponse.ok) {
        setUser(userResponse.body);
      } else {
        // Fallback: manually update user context with response data
        setUser({
          ...user,
          about_me: response.data?.about_me,
          soundcloud_url: response.data?.soundcloud_url,
          user_type: response.data?.user_type?.join(',') || '',
        });
      }

      enqueueSnackbar('Profile updated!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to update profile', { variant: 'error' });
    } finally {
      setEditProfileOpen(false);
    }
  };

  return (
    <>
      <PageHeader backgroundArt={defaultArt} minHeight={350}>
        <InternalPageMeta>
          <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }}>
            <OptionsMenu
              options={[
                {
                  label: 'Edit Profile',
                  icon: <EditIcon fontSize="small" />,
                  action: () => {
                    setEditProfile({
                      username: user?.username || '',
                      about_me: user?.about_me || '',
                      soundcloud_url: user?.soundcloud_url || '',
                      user_type:
                        user?.user_type &&
                        typeof user.user_type === 'string' &&
                        user.user_type.length > 0
                          ? user.user_type.split(',')
                          : ['listener'],
                    });
                    setEditProfileOpen(true);
                  },
                },
                {
                  label: 'Notification Preferences',
                  icon: <SettingsIcon fontSize="small" />,
                  action: () => {
                    setNotificationPreferences({
                      notification_timeframe:
                        user?.notification_timeframe || 'daily',
                      notification_preferences_enabled:
                        user?.notification_preferences_enabled ?? true,
                    });
                    setNotificationModalOpen(true);
                  },
                },
              ]}
            />
          </Box>
          <InternalPageArt src={avatar_url || defaultArt} />
          <InternalPageText>{username}</InternalPageText>
          {about_me && <ShowMore label="Bio" content={about_me} />}
          {user?.soundcloud_url && (
            <Stack direction={'row'} spacing={2} my={2}>
              <SocialLinks data={{ soundcloud: user.soundcloud_url }} />
            </Stack>
          )}
        </InternalPageMeta>
      </PageHeader>
      {/* Edit Profile Modal */}
      <Dialog
        open={editProfileOpen}
        onClose={() => setEditProfileOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <Box component="form" onSubmit={handleEditProfileSubmit} sx={{ p: 3 }}>
          <Typography variant="h6" mb={2}>
            Edit Profile
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="About"
              name="about_me"
              value={editProfile.about_me}
              onChange={handleEditProfileChange}
              fullWidth
              size="small"
              variant="outlined"
              multiline
              minRows={2}
              autoComplete="off"
            />
            <TextField
              label="SoundCloud Profile URL"
              name="soundcloud_url"
              value={editProfile.soundcloud_url}
              onChange={handleEditProfileChange}
              fullWidth
              size="small"
              variant="outlined"
              autoComplete="off"
            />
            <FormLabel component="legend" sx={{ mt: 1 }}>
              How do you describe yourself? (Check all that apply)
            </FormLabel>
            <FormGroup>
              {SOUND_CLOUD_OPTIONS.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      checked={editProfile.user_type.includes(opt.value)}
                      onChange={handleEditProfileUserTypeChange}
                      value={opt.value}
                      color="primary"
                    />
                  }
                  label={opt.label}
                />
              ))}
            </FormGroup>
            <Button type="submit" variant="contained" color="primary" fullWidth>
              Update
            </Button>
          </Stack>
        </Box>
      </Dialog>
      {/* Notification Preferences Modal */}
      <Dialog
        open={notificationModalOpen}
        onClose={() => setNotificationModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <Box
          component="form"
          onSubmit={handleNotificationPreferencesUpdate}
          sx={{ p: 3 }}
        >
          <Typography variant="h6" mb={2}>
            Notification Preferences
          </Typography>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    notificationPreferences.notification_preferences_enabled
                  }
                  onChange={handleCheckboxChange}
                  color="primary"
                />
              }
              label="Enable email notifications"
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Notification Frequency
              </Typography>
              <Select
                value={notificationPreferences.notification_timeframe}
                onChange={handleSelectChange}
                size="small"
                fullWidth
                sx={{ color: 'text.primary', bgcolor: 'background.default' }}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </Box>
            <Button type="submit" variant="contained" color="primary" fullWidth>
              Update Preferences
            </Button>
          </Stack>
        </Box>
      </Dialog>
      <TabsContainer
        tabTitles={[
          { title: 'Liked Sets' },
          { title: 'Liked Tracks' },
          { title: 'Purchased Tracks' },
          { title: 'Watched IDs' },
          { title: 'Liked Artists' },
          { title: 'Liked Labels' },
        ]}
        tabContent={[
          <SetsSection
            dataFrom={`/users/${id}/sets`}
            showFavoritesFilter
            key={'sets'}
          />,
          <TracksSection
            dataFrom={`/users/${id}/tracks`}
            showFavoritesFilter
            key={'tracks'}
          />,
          <TracksSection
            dataFrom={`/users/${id}/tracks/purchased`}
            showFavoritesFilter
            key={'purchased'}
          />,
          <IdsSection
            dataFrom={`/users/${id}/ids`}
            showFavoritesFilter
            key={'ids'}
          />,
          <ArtistsSection
            dataFrom={`/users/${id}/artists`}
            showFollowedFilter
            key={'artists'}
          />,
          <LabelsSection
            dataFrom={`/users/${id}/labels`}
            showFollowedFilter
            key={'labels'}
          />,
        ]}
      />
    </>
  );
};

export default Profile;
